import { memo } from 'react';

import {
  getShareLinkPublicColorMode,
  type ShareLinkColorMode,
} from '~/components/v2/components/shared/share-link-design/shareLinkColorMode';
import { usePublicShareLinkForcedTheme } from '~/components/v2/store/features/share-link-theme/usePublicShareLinkForcedTheme';

export type PublicBoardForcedThemeProps = {
  colorMode: ShareLinkColorMode | undefined;
};

/*
 * Invisible mount point that forces the document theme to a board share link's `colorMode`
 * (`presentationSettings.colorMode`) while the public board route is on screen — the board
 * counterpart of `PublicShowcasePage` calling `usePublicShareLinkForcedTheme` directly.
 *
 * Rendered by the share route (`/a/[[...slug]]`) ABOVE the `showUI2` swap between
 * `PublicBoardLayout` (v2) and the legacy `PublicBoard` tree, not inside either layout: the swap
 * unmounts one tree and mounts the other when the viewport crosses the medium breakpoint, and a
 * per-tree mount would run the outgoing tree's store-clearing cleanup mid-swap — transiently
 * snapping the document back to the visitor's theme (same class of bug as the `ReCaptchaProvider`
 * hoist at the render site). One mount above the swap stays put, and since forcing happens at the
 * document level (store → `AppThemeProvider`), it themes both trees.
 *
 * On a normal ISR load this store write is redundant — `getShareLinkForcedTheme` derives the same
 * value from pageProps before first paint. It exists for the data the server can never see:
 * passcode-protected board links, whose presentationSettings arrive only after the visitor
 * verifies (`fetchPublicBoardShareData` → `setInitialBoardData`).
 */
export const PublicBoardForcedTheme = memo(({ colorMode }: PublicBoardForcedThemeProps) => {
  // Absent colorMode (legacy links) is Light, matching the public collection page — this mount
  // always owns the document, so the public default is safe to apply.
  usePublicShareLinkForcedTheme(getShareLinkPublicColorMode(colorMode));

  return null;
});

PublicBoardForcedTheme.displayName = 'PublicBoardForcedTheme';
